import { PDFDocument, rgb, StandardFonts } from 'pdf-lib';
import { saveAs } from 'file-saver';
import { apiService } from '../services/ApiService';

/**
 * Formatação de valores em moeda brasileira
 * @param {number} valor 
 * @returns {string} Valor formatado
 */
export const formatarValor = (valor) => {
  return valor.toLocaleString('pt-BR', {
    style: 'currency',
    currency: 'BRL'
  });
};

/**
 * Quebra um texto em múltiplas linhas
 * @param {string} texto - Texto a ser quebrado
 * @param {number} maxCaracteres - Número máximo de caracteres por linha
 * @returns {Array<string>} - Array com as linhas quebradas
 */
const quebrarTexto = (texto, maxCaracteres) => {
  if (!texto || texto.length <= maxCaracteres) return [texto || ''];
  
  const linhas = [];
  let linhaAtual = '';
  const palavras = texto.split(' ');
  
  for (const palavra of palavras) {
    const novaLinha = linhaAtual ? linhaAtual + ' ' + palavra : palavra;
    
    if (novaLinha.length <= maxCaracteres) {
      linhaAtual = novaLinha;
    } else {
      linhas.push(linhaAtual);
      linhaAtual = palavra;
    }
  }
  
  if (linhaAtual) {
    linhas.push(linhaAtual);
  }
  
  return linhas;
};

/**
 * Calcula os totais do orçamento
 * @param {Array|Object} projectItems - Itens do projeto
 * @returns {Object} Totais calculados
 */
const calcularTotais = (projectItems) => {
  // Verificar se projectItems é um objeto com valores pré-calculados
  if (!Array.isArray(projectItems) && projectItems.valorTotalGeral !== undefined) {
    return {
      totalMaterial: Number(projectItems.valorTotalMaterial || 0),
      totalMaoDeObra: Number(projectItems.valorTotalMO || 0),
      totalGeral: Number(projectItems.valorTotalGeral || 0)
    };
  }

  // Garantir que projectItems seja um array para o cálculo manual
  const itensArray = Array.isArray(projectItems) ? projectItems : [];
  
  // Caso contrário, calcule os totais manualmente
  let totalMaterial = 0;
  let totalMaoDeObra = 0;
  
  // Filtra apenas itens que não são seções
  const itens = itensArray.filter(item => item.tipo !== 'secao');
  
  itens.forEach(item => {
    // Garantir que os valores sejam números válidos
    const materialTotal = parseFloat(item.materialTotal || item.materialUnitario * item.quantidade || 0) || 0;
    const moTotal = parseFloat(item.moTotal || item.moUnitario * item.quantidade || 0) || 0;
    
    // Calcular totais considerando quantidade
    const quantidade = parseFloat(item.quantidade || 1);
    
    // Se já temos o total calculado, usamos diretamente, senão multiplicamos unitário por quantidade
    if (item.materialTotal !== undefined) {
      totalMaterial += materialTotal;
    } else {
      totalMaterial += (parseFloat(item.materialUnitario || 0) || 0) * quantidade;
    }
    
    if (item.moTotal !== undefined) {
      totalMaoDeObra += moTotal;
    } else {
      totalMaoDeObra += (parseFloat(item.moUnitario || 0) || 0) * quantidade;
    }
  });
  
  const totalGeral = totalMaterial + totalMaoDeObra;
  
  return {
    totalMaterial: Number(totalMaterial.toFixed(2)),
    totalMaoDeObra: Number(totalMaoDeObra.toFixed(2)),
    totalGeral: Number(totalGeral.toFixed(2))
  };
};

/**
 * Renderiza uma seção e seus itens no PDF
 * @param {Object} pdfDoc - Documento PDF
 * @param {Object} page - Página atual do PDF
 * @param {number} margin - Margem das páginas
 * @param {number} width - Largura útil da página
 * @param {number} yPos - Posição vertical atual
 * @param {Object} secao - Dados da seção
 * @param {Array} itens - Itens da seção
 * @param {Object} fonts - Fontes disponíveis
 * @param {Object} colors - Cores do documento
 * @returns {number} Nova posição Y após renderizar a seção
 */
const renderizarSecao = (pdfDoc, page, margin, width, yPos, secao, itens, fonts, colors) => {
  // Altura mínima segura para conteúdo na página
  const alturaMinima = margin + 100;
  
  // Verificar se temos espaço para o título da seção
  if (yPos < alturaMinima) {
    page = pdfDoc.addPage([595.28, 841.89]);
    yPos = page.getHeight() - margin;
  }
  
  // Desenhar título da seção
  page.drawRectangle({
    x: margin,
    y: yPos - 20,
    width: width,
    height: 20,
    color: rgb(0.95, 0.95, 0.95)
  });
  
  page.drawText(secao.descricao || 'Seção sem título', {
    x: margin + 10,
    y: yPos - 15,
    size: 12,
    font: fonts.bold,
    color: rgb(0.3, 0.3, 0.3)
  });
  
  yPos -= 30;
  
  // Se não há itens, mostramos mensagem
  if (!itens || itens.length === 0) {
    page.drawText('Nenhum item cadastrado para esta seção', {
      x: margin + 10,
      y: yPos - 10,
      size: 10,
      font: fonts.regular,
      color: rgb(0.5, 0.5, 0.5)
    });
    
    yPos -= 25;
    return { page, yPos };
  }

  // Configurações da tabela
  const colunas = {
    descricao: { x: margin + 5, largura: width * 0.45 },
    unidade: { x: margin + width * 0.45 - 11 },
    quantidade: { x: margin + width * 0.53 - 11 },
    matUnitario: { x: margin + width * 0.61 - 17 },
    matTotal: { x: margin + width * 0.69 - 12 },
    moUnitario: { x: margin + width * 0.77 - 7 },
    moTotal: { x: margin + width * 0.85 + 5 }
  };
  
  // Desenhar cabeçalho da tabela
  const desenharCabecalho = (posY) => {
    page.drawRectangle({
      x: margin,
      y: posY - 20,
      width: width,
      height: 20,
      color: rgb(0.95, 0.95, 0.95)
    });
    
    page.drawText('Descrição', {
      x: colunas.descricao.x,
      y: posY - 15,
      size: 9,
      font: fonts.bold,
      color: rgb(0.3, 0.3, 0.3)
    });
    
    page.drawText('Un.', {
      x: colunas.unidade.x,
      y: posY - 15,
      size: 9,
      font: fonts.bold,
      color: rgb(0.3, 0.3, 0.3)
    });
    
    page.drawText('Qtd.', {
      x: colunas.quantidade.x,
      y: posY - 15,
      size: 9,
      font: fonts.bold,
      color: rgb(0.3, 0.3, 0.3)
    });
    
    page.drawText('Mat. Unit.', {
      x: colunas.matUnitario.x,
      y: posY - 15,
      size: 9,
      font: fonts.bold,
      color: rgb(0.3, 0.3, 0.3)
    });
    
    page.drawText('Mat. Total', {
      x: colunas.matTotal.x,
      y: posY - 15,
      size: 9,
      font: fonts.bold,
      color: rgb(0.3, 0.3, 0.3)
    });
    
    page.drawText('MO Unit.', {
      x: colunas.moUnitario.x,
      y: posY - 15,
      size: 9,
      font: fonts.bold,
      color: rgb(0.3, 0.3, 0.3)
    });
    
    page.drawText('MO Total', {
      x: colunas.moTotal.x,
      y: posY - 15,
      size: 9,
      font: fonts.bold,
      color: rgb(0.3, 0.3, 0.3)
    });
    
    return posY - 30;
  };
  
  // Desenhar o cabeçalho inicial
  yPos = desenharCabecalho(yPos);
  
  // Acumuladores de subtotais
  let subtotalMaterial = 0;
  let subtotalMO = 0;
  
  // Constantes para cálculos de altura
  const alturaLinha = 13;
  
  // Função para criar nova página
  const criarNovaPagina = () => {
    page = pdfDoc.addPage([595.28, 841.89]);
    const novoY = page.getHeight() - margin;
    
    // Redesenhar título da seção
    page.drawRectangle({
      x: margin,
      y: novoY - 20,
      width: width,
      height: 20,
      color: rgb(0.95, 0.95, 0.95)
    });
    
    page.drawText(secao.descricao || 'Seção sem título', {
      x: margin + 10,
      y: novoY - 15,
      size: 12,
      font: fonts.bold,
      color: rgb(0.3, 0.3, 0.3)
    });
    
    // Redesenhar cabeçalho da tabela
    return desenharCabecalho(novoY - 30);
  };
  
  // Itens da tabela
  for (const item of itens) {
    // Quebrar descrição em linhas
    const descricaoMaxima = 34;
    const linhasDescricao = quebrarTexto(item.descricao || 'Item sem descrição', descricaoMaxima);
    
    // Verificar se existe observação e quebrar em linhas
    let linhasObs = [];
    if (item.observacao) {
      const obsTexto = "Obs: " + item.observacao;
      linhasObs = quebrarTexto(obsTexto, descricaoMaxima);
    }
    
    // Calcular altura total do item
    const alturaDescricao = linhasDescricao.length * alturaLinha;
    const espacoExtra = linhasObs.length > 0 ? 10 : 0;
    const alturaObservacao = linhasObs.length > 0 ? (linhasObs.length * alturaLinha) : 0;
    const alturaItem = alturaDescricao + espacoExtra + alturaObservacao + 40;
    
    // IMPORTANTE: Verificar se o item cabe na página ANTES de começar a desenhar
    if (yPos - alturaItem < alturaMinima) {
      yPos = criarNovaPagina();
    }
    
    // Desenhar descrição (múltiplas linhas)
    for (let i = 0; i < linhasDescricao.length; i++) {
      page.drawText(linhasDescricao[i], {
        x: colunas.descricao.x,
        y: yPos - 15 - (i * alturaLinha),
        size: 9,
        font: fonts.regular,
        color: rgb(0.3, 0.3, 0.3)
      });
    }
    
    // Desenhar observação, se existir
    if (linhasObs.length > 0) {
      const obsY = yPos - 15 - (linhasDescricao.length * alturaLinha) - espacoExtra;
      
      for (let i = 0; i < linhasObs.length; i++) {
        if (i === 0 && linhasObs[i].startsWith("Obs:")) {
          const parts = linhasObs[i].split(":");
          if (parts.length > 1) {
            page.drawText("Obs:", {
              x: colunas.descricao.x,
              y: obsY - (i * alturaLinha),
              size: 7,
              font: fonts.bold,
              color: colors.observacao
            });
            
            page.drawText(parts.slice(1).join(":"), {
              x: colunas.descricao.x + 25,
              y: obsY - (i * alturaLinha),
              size: 7,
              font: fonts.regular,
              color: colors.observacao
            });
          } else {
            page.drawText(linhasObs[i], {
              x: colunas.descricao.x,
              y: obsY - (i * alturaLinha),
              size: 7,
              font: fonts.regular,
              color: colors.observacao
            });
          }
        } else {
          page.drawText(linhasObs[i], {
            x: colunas.descricao.x + 25,
            y: obsY - (i * alturaLinha),
            size: 7,
            font: fonts.regular,
            color: colors.observacao
          });
        }
      }
    }
    
    // Formatar valores
    const quantidadeNum = parseFloat(item.quantidade || 0);
    const quantidade = Number.isInteger(quantidadeNum) ? quantidadeNum.toString() : quantidadeNum.toFixed(2);
    const valorMatUnit = parseFloat(item.materialUnitario || 0);
    const qtd = parseFloat(item.quantidade || 0);
    const valorMatTotal = item.materialTotal ? parseFloat(item.materialTotal) : (valorMatUnit * qtd);
    const valorMoUnit = parseFloat(item.moUnitario || 0);
    const valorMoTotal = item.moTotal ? parseFloat(item.moTotal) : (valorMoUnit * qtd);
    
    // Desenhar valores na linha
    page.drawText(item.unidade || '', {
      x: colunas.unidade.x,
      y: yPos - 15,
      size: 9,
      font: fonts.regular,
      color: rgb(0.3, 0.3, 0.3)
    });
    
    page.drawText(quantidade, {
      x: colunas.quantidade.x,
      y: yPos - 15,
      size: 9,
      font: fonts.regular,
      color: rgb(0.3, 0.3, 0.3)
    });
    
    // Verificar tamanho dos valores e ajustar tamanho da fonte se necessário
    const tamFonteValores = (valor) => {
      switch (true) {
        case valor >= 100000:
          return 7; // Valores >= R$ 100.000,00
        case valor >= 50000:
          return 7.5; // Valores >= R$ 50.000,00
        case valor >= 10000:
          return 8; // Valores >= R$ 10.000,00
        case valor >= 999:
          return 7.5; // Valores >= R$ 999,00
        default:
          return 9; // Valores < R$ 999,00
      }
    };
    
    page.drawText(formatarValor(valorMatUnit), {
      x: colunas.matUnitario.x,
      y: yPos - 15,
      size: tamFonteValores(valorMatUnit),
      font: fonts.regular,
      color: rgb(0.3, 0.3, 0.3)
    });
    
    page.drawText(formatarValor(valorMatTotal), {
      x: colunas.matTotal.x,
      y: yPos - 15,
      size: tamFonteValores(valorMatTotal),
      font: fonts.regular,
      color: rgb(0.3, 0.3, 0.3)
    });
    
    page.drawText(formatarValor(valorMoUnit), {
      x: colunas.moUnitario.x,
      y: yPos - 15,
      size: tamFonteValores(valorMoUnit),
      font: fonts.regular,
      color: rgb(0.3, 0.3, 0.3)
    });
    
    page.drawText(formatarValor(valorMoTotal), {
      x: colunas.moTotal.x,
      y: yPos - 15,
      size: tamFonteValores(valorMoTotal),
      font: fonts.regular,
      color: rgb(0.3, 0.3, 0.3)
    });
    
    // Linha separadora
    const linhaY = yPos - alturaItem + 10;
    page.drawLine({
      start: { x: margin, y: linhaY },
      end: { x: margin + width, y: linhaY },
      thickness: 0.5,
      color: rgb(0.9, 0.9, 0.9)
    });
    
    // Acumular subtotais
    subtotalMaterial += valorMatTotal;
    subtotalMO += valorMoTotal;
    
    // Atualizar posição Y para o próximo item
    yPos = linhaY - 15;
  }
  
  // Verificar se temos espaço para subtotais
  if (yPos < alturaMinima) {
    yPos = criarNovaPagina();
  }
  
  // Adicionar subtotais da seção
  yPos -= 15;
  
  page.drawText('Subtotal Material:', {
    x: colunas.matTotal.x - 120,
    y: yPos,
    size: 9,
    font: fonts.bold,
    color: colors.primaria
  });
  
  page.drawText(formatarValor(subtotalMaterial), {
    x: colunas.matTotal.x - 40,
    y: yPos,
    size: tamFonteValores(subtotalMaterial),
    font: fonts.bold,
    color: colors.primaria
  });
  
  page.drawText('Subtotal MO:', {
    x: colunas.moTotal.x - 60,
    y: yPos,
    size: 9,
    font: fonts.bold,
    color: colors.primaria
  });
  
  page.drawText(formatarValor(subtotalMO), {
    x: colunas.moTotal.x,
    y: yPos,
    size: tamFonteValores(subtotalMO),
    font: fonts.bold,
    color: colors.primaria
  });
  
  // Espaço adicional após a seção
  yPos -= 35;
  
  return { page, yPos };
};

/**
 * Carrega uma imagem de uma URL e a converte para o formato do PDF
 * @param {string} url - URL da imagem
 * @param {PDFDocument} pdfDoc - Documento PDF
 * @returns {Promise<PDFImage>} - Imagem carregada
 */
const carregarImagemDeURL = async (url, pdfDoc) => {
  try {
    const response = await fetch(url);
    const arrayBuffer = await response.arrayBuffer();
    const image = await pdfDoc.embedPng(arrayBuffer);
    return image;
  } catch (error) {
    console.error('Erro ao carregar imagem:', error);
    throw error;
  }
};

/**
 * Gera um PDF para o orçamento atual
 * @param {Object} activeProject - Projeto ativo
 * @param {Array} projectItems - Itens do projeto
 * @param {string} empresaNome - Nome da empresa
 * @param {string} empresaCNPJ - CNPJ da empresa
 */
export const gerarPDF = async (activeProject, projectItems, empresaNome = 'KMF ENGENHARIA', empresaCNPJ = 'XX.XXX.XXX/0001-XX') => {
  if (!activeProject) {
    console.error('Nenhum orçamento selecionado');
    return;
  }
  
  try {
    // Buscar configurações da empresa
    let configEmpresa = null;
    try {
      const resultado = await apiService.getDiarioObraConfiguracao();
      if (resultado.success && resultado.data) {
        configEmpresa = resultado.data;
      }
    } catch (error) {
      console.error('Erro ao carregar configurações da empresa:', error);
    }
    
    // Criar documento
    const pdfDoc = await PDFDocument.create();
    
    // Carregar fontes
    const fonts = {
      regular: await pdfDoc.embedFont(StandardFonts.Helvetica),
      bold: await pdfDoc.embedFont(StandardFonts.HelveticaBold)
    };
    
    // Definir esquema de cores
    const colors = {
      primaria: configEmpresa?.corPrimaria ? rgb(
        parseInt(configEmpresa.corPrimaria.slice(1, 3), 16) / 255,
        parseInt(configEmpresa.corPrimaria.slice(3, 5), 16) / 255,
        parseInt(configEmpresa.corPrimaria.slice(5, 7), 16) / 255
      ) : rgb(254/255, 129/255, 56/255), // Laranja KMF como fallback
      secundaria: rgb(0.3, 0.3, 0.3),
      observacao: rgb(0.9, 0.2, 0.2) // Vermelho para observações
    };
    
    // Configurações de página
    const margin = 50;
    const pageDimensions = [595.28, 841.89]; // A4
    
    // Criar primeira página
    let page = pdfDoc.addPage(pageDimensions);
    const width = page.getWidth() - 2 * margin;
    let yPos = page.getHeight() - margin;
    
    // Data atual formatada
    const dataAtual = new Date().toLocaleDateString('pt-BR');
    
    // Adicionar logo se disponível
    if (configEmpresa?.logoEmpresas) {
      try {
        const logo = await carregarImagemDeURL(configEmpresa.logoEmpresas, pdfDoc);
        const logoAspectRatio = logo.width / logo.height;
        const logoMaxWidth = 150;
        const logoMaxHeight = 50;
        
        let logoWidth, logoHeight;
        
        if (logoAspectRatio > 1) {
          logoWidth = Math.min(logoMaxWidth, logo.width);
          logoHeight = logoWidth / logoAspectRatio;
        } else {
          logoHeight = Math.min(logoMaxHeight, logo.height);
          logoWidth = logoHeight * logoAspectRatio;
        }
        
        const centerX = (page.getWidth() - logoWidth) / 2;
        
        page.drawImage(logo, {
          x: centerX,
          y: yPos - logoHeight,
          width: logoWidth,
          height: logoHeight
        });
        
        yPos -= (logoHeight + 20);
      } catch (error) {
        console.warn('Erro ao carregar logo:', error);
      }
    }
    
    // Cabeçalho da primeira página
    page.drawText(empresaNome, {
      x: margin,
      y: yPos - 20,
      size: 16,
      font: fonts.bold,
      color: colors.primaria
    });
    
    page.drawText('PLANILHA ORÇAMENTÁRIA', {
      x: margin,
      y: yPos - 40,
      size: 12,
      font: fonts.bold,
      color: colors.secundaria
    });
    
    page.drawText(`Projeto: ${activeProject.name || 'Sem nome'}`, {
      x: margin,
      y: yPos - 70,
      size: 12,
      font: fonts.bold,
      color: colors.primaria
    });
    
    page.drawText(`Data: ${dataAtual}`, {
      x: page.getWidth() - margin - 100,
      y: yPos - 70,
      size: 10,
      font: fonts.regular,
      color: colors.secundaria
    });
    
    page.drawLine({
      start: { x: margin, y: yPos - 90 },
      end: { x: page.getWidth() - margin, y: yPos - 90 },
      thickness: 1,
      color: colors.primaria
    });
    
    // Posição inicial para começar a renderizar seções
    yPos = yPos - 120;
    
    // Separar seções e itens sem seção
    const secoes = projectItems.filter(item => item.tipo === 'secao');
    const itensSemSecao = projectItems.filter(item => item.tipo !== 'secao' && (!item.secaoId || item.secaoId === ''));
    
    // Renderizar seções
    if (secoes.length === 0) {
      // Se não houver seções, criar uma padrão com todos os itens
      const secaoPadrao = {
        id: 'geral',
        descricao: 'Serviços gerais',
        tipo: 'secao'
      };
      
      const itensGerais = projectItems.filter(item => item.tipo !== 'secao');
      
      const resultado = renderizarSecao(
        pdfDoc, page, margin, width, yPos, 
        secaoPadrao, itensGerais, fonts, colors
      );
      
      page = resultado.page;
      yPos = resultado.yPos;
    } else {
      // Renderizar cada seção
      for (const secao of secoes) {
        // Encontrar itens para esta seção
        const itensDaSecao = projectItems.filter(item => {
          if (item.tipo === 'secao') return false;
          const itemSecaoId = item.secaoId ? String(item.secaoId) : '';
          const secaoId = secao.id ? String(secao.id) : '';
          return itemSecaoId === secaoId;
        });
        
        // Renderizar seção com seus itens
        const resultado = renderizarSecao(
          pdfDoc, page, margin, width, yPos, 
          secao, itensDaSecao, fonts, colors
        );
        
        page = resultado.page;
        yPos = resultado.yPos;
      }
      
      // Renderizar itens sem seção, se houver
      if (itensSemSecao.length > 0) {
        const secaoSemCategoria = {
          id: 'sem-secao',
          descricao: 'Itens sem seção',
          tipo: 'secao'
        };
        
        const resultado = renderizarSecao(
          pdfDoc, page, margin, width, yPos, 
          secaoSemCategoria, itensSemSecao, fonts, colors
        );
        
        page = resultado.page;
        yPos = resultado.yPos;
      }
    }
    
    // Calcular totais
    const totais = calcularTotais(projectItems);
    
    // Verificar se temos espaço para o resumo (mínimo 200px)
    const alturaResumo = 200;
    
    if (yPos < margin + alturaResumo) {
      // Criar nova página para o resumo
      page = pdfDoc.addPage(pageDimensions);
      yPos = page.getHeight() - margin;
    }
    
    // Adicionar linha separadora para resumo
    page.drawLine({
      start: { x: margin, y: yPos + 10 },
      end: { x: page.getWidth() - margin, y: yPos + 10 },
      thickness: 1,
      color: colors.primaria
    });
    
    yPos -= 30;
    
    // Título do resumo
    page.drawRectangle({
      x: margin,
      y: yPos - 20,
      width: width,
      height: 20,
      color: rgb(0.95, 0.95, 0.95)
    });
    
    page.drawText('RESUMO GERAL', {
      x: margin + 10,
      y: yPos - 15,
      size: 12,
      font: fonts.bold,
      color: colors.primaria
    });
    
    yPos -= 40;
    
    // Função para criar linha do resumo
    const desenharLinhaResumo = (label, valor) => {
      page.drawRectangle({
        x: margin,
        y: yPos - 20,
        width: width,
        height: 20,
        color: rgb(0.97, 0.97, 0.97)
      });
      
      page.drawText(label, {
        x: margin + 10,
        y: yPos - 15,
        size: 10,
        font: fonts.bold,
        color: colors.secundaria
      });
      
      // Verificar se precisa diminuir o tamanho da fonte
      const tamanhoFonte = valor >= 10000 ? 9 : 10;
      
      page.drawText(formatarValor(valor), {
        x: margin + width - 100,
        y: yPos - 15,
        size: tamanhoFonte,
        font: fonts.bold,
        color: colors.primaria
      });
      
      yPos -= 30;
    };
    
    // Linhas do resumo
    desenharLinhaResumo('Total Material:', totais.totalMaterial);
    desenharLinhaResumo('Total Mão de Obra:', totais.totalMaoDeObra);
    
    // Total geral com destaque
    page.drawRectangle({
      x: margin,
      y: yPos - 25,
      width: width,
      height: 25,
      color: rgb(0.9, 0.9, 0.9)
    });
    
    page.drawText('TOTAL GERAL:', {
      x: margin + 10,
      y: yPos - 17,
      size: 12,
      font: fonts.bold,
      color: colors.secundaria
    });
    
    // Verificar se o valor total tem 5 dígitos ou mais
    const tamanhoFonteTotal = tamFonteTotalGeral(totais.totalGeral);
    
    page.drawText(formatarValor(totais.totalGeral), {
      x: margin + width - 100,
      y: yPos - 17,
      size: tamanhoFonteTotal,
      font: fonts.bold,
      color: colors.primaria
    });
    
    // Adicionar rodapé em todas as páginas
    const totalPages = pdfDoc.getPageCount();
    
    for (let i = 0; i < totalPages; i++) {
      const currentPage = pdfDoc.getPage(i);
      const rodapeY = 35;
      
      currentPage.drawLine({
        start: { x: margin, y: rodapeY + 15 },
        end: { x: currentPage.getWidth() - margin, y: rodapeY + 15 },
        thickness: 0.5,
        color: rgb(0.8, 0.8, 0.8)
      });
      
      currentPage.drawText(`${empresaNome}`, {
        x: margin,
        y: rodapeY,
        size: 8,
        font: fonts.bold,
        color: rgb(0.5, 0.5, 0.5)
      });
      
      currentPage.drawText(`CNPJ: ${empresaCNPJ}`, {
        x: margin,
        y: rodapeY - 10,
        size: 8,
        font: fonts.regular,
        color: rgb(0.5, 0.5, 0.5)
      });
      
      currentPage.drawText(`Documento gerado em ${dataAtual}`, {
        x: currentPage.getWidth() - margin - 150,
        y: rodapeY,
        size: 8,
        font: fonts.regular,
        color: rgb(0.5, 0.5, 0.5)
      });
      
      currentPage.drawText(`Página ${i + 1} de ${totalPages}`, {
        x: currentPage.getWidth() - margin - 150,
        y: rodapeY - 10,
        size: 8,
        font: fonts.regular,
        color: rgb(0.5, 0.5, 0.5)
      });
    }
    
    // Salvar e fazer download do PDF
    const pdfBytes = await pdfDoc.save();
    const blob = new Blob([pdfBytes], { type: 'application/pdf' });
    saveAs(blob, `Orcamento_${activeProject.name.replace(/\s+/g, '_')}.pdf`);
    
    return true;
  } catch (error) {
    console.error('Erro ao gerar PDF:', error);
    alert('Não foi possível gerar o PDF. Verifique se as bibliotecas pdf-lib e file-saver estão instaladas. Erro: ' + error.message);
    return false;
  }
};

// Função para determinar o tamanho da fonte do total geral
const tamFonteTotalGeral = (valor) => {
  switch (true) {
    case valor >= 100000:
      return 9; // Valores >= R$ 100.000,00
    case valor >= 50000:
      return 10; // Valores >= R$ 50.000,00
    case valor >= 10000:
      return 11; // Valores >= R$ 10.000,00
    default:
      return 12; // Valores < R$ 10.000,00
  }
};

// Função para determinar o tamanho da fonte baseado no valor
const tamFonteValores = (valor) => {
  switch (true) {
    case valor >= 100000:
      return 7; // Valores >= R$ 100.000,00
    case valor >= 50000:
      return 7.5; // Valores >= R$ 50.000,00
    case valor >= 10000:
      return 8; // Valores >= R$ 10.000,00
    case valor >= 999:
      return 7.5; // Valores >= R$ 999,00
    default:
      return 9; // Valores < R$ 999,00
  }
}; 